import { FC, Suspense, useEffect } from "react";
import { useFragment } from "react-relay";
import { useLocation } from "react-router-dom";
import { usePreloadedMetadataAssets } from "../common/components/Balance";
import { RedactedScope, useRedacted } from "../services/redacted";
import {
  AccountDataContext,
  usePreloadedAccountData,
} from "../services/accountData";
import {
  headerAccountValue,
  HeaderAccountFragment,
} from "../common/components/Header";
import type { HeaderAccountFragment$key } from "../common/components/__generated__/HeaderAccountFragment.graphql";
import { useRedactedV2 } from "./v2";
import { positionForSection, sectionOf } from "./v2/positionRegistry";
import { privateWalletMode } from "./v2/rotation";
import "./ModelBScope.scss";

/** A claw-back below this USD value is not worth its relayer gas ($0.05). */
const DUST_USD = 5_000_000n; // 8-decimal USD, as Fiat renders valueUsd

/**
 * Model B page scope. Product pages behave like public Rujira: a page with an
 * existing position (stake, loan, orders) shows it and acts on its sub; every
 * other page acts from a fresh one-time sub. Invisible to the user.
 */
export const ModelBScope: FC = () => {
  const { pathname } = useLocation();
  const { isPrivateMode } = useRedacted();
  const v2 = useRedactedV2();
  const { enabled, positions, pinned, pinnedPath, pin, setDustPolicy } =
    v2.modelB;
  const assets = usePreloadedMetadataAssets();
  const section = sectionOf(pathname);

  // Development only: inspect the live session from the console / tests.
  if (import.meta.env.DEV)
    (globalThis as unknown as { __rdV2?: unknown }).__rdV2 = v2;

  useEffect(() => {
    if (!enabled || !isPrivateMode || v2.status !== "ready") return;
    // Single mode: every page acts on the Spending sub. A portfolio row may
    // manage an older account's own position from that account; the choice
    // ends with the page it was made on, so no new action ever uses it.
    if (privateWalletMode() === "single") {
      if (pinned && pinnedPath !== pathname) pin(undefined);
      return;
    }
    // The portfolio lists every position; its rows scope their own actions.
    if (section.startsWith("portfolio")) return;
    const target = positionForSection(positions, section);
    if (target === pinned) return;
    try {
      pin(target);
    } catch {
      /* A position sub that is no longer owned: stay on a fresh sub. */
    }
  }, [
    enabled,
    isPrivateMode,
    pathname,
    pin,
    pinned,
    pinnedPath,
    positions,
    section,
    v2.status,
  ]);

  useEffect(() => {
    const active = enabled && isPrivateMode;
    document.body.classList.toggle("rd-model-b", active);
    return () => document.body.classList.remove("rd-model-b");
  }, [enabled, isPrivateMode]);

  useEffect(() => {
    if (!enabled) return;
    const prices = new Map(
      assets.flatMap((asset) => {
        const denom = asset.variants?.native?.denom;
        return denom && asset.price?.current
          ? [[denom, asset.price.current] as const]
          : [];
      })
    );
    setDustPolicy((coin) => {
      const price = prices.get(coin.denom);
      // Unknown price: only skip literal dust, return everything else.
      if (!price) return BigInt(coin.amount) < 1_000n;
      return (BigInt(coin.amount) * price) / 10n ** 12n < DUST_USD;
    });
  }, [assets, enabled, setDustPolicy]);

  // Every position sub reports its open position value (orders, loans,
  // strategies, stakes) so totals match public Rujira on every page.
  if (!enabled || !isPrivateMode || v2.status !== "ready") return null;
  return (
    <>
      {v2.modelB.scopes.map((subwallet) => (
        <RedactedScope key={subwallet} subWalletAddress={subwallet}>
          <AccountDataContext>
            <Suspense fallback={null}>
              <PositionValueReporter subwallet={subwallet} />
            </Suspense>
          </AccountDataContext>
        </RedactedScope>
      ))}
    </>
  );
};

const PositionValueReporter: FC<{ subwallet: string }> = ({ subwallet }) => {
  const { accountData } = usePreloadedAccountData(false);
  const account = useFragment<HeaderAccountFragment$key>(
    HeaderAccountFragment,
    accountData
  );
  const report = useRedactedV2().modelB.reportPositionValue;
  useEffect(() => {
    if (account) report(subwallet, headerAccountValue(account));
  }, [account, report, subwallet]);
  return null;
};
